import Api from './axiosInstance'
import { API_USER_MGMT } from 'utils/constants'

const { USER, UPDATE_COLS, ALLOWED_PAGES, ASSIGNABLE_FRESH_CATEGORIES } =
  API_USER_MGMT

export type RoleAllowedPages = {
  role: string
  // true when the role may ONLY hold the pages listed below (an empty list
  // means the role holds no admin pages at all, e.g. FRESH_STOCKER)
  isRestricted: boolean
  pages: string[]
}

// Session cache, keyed by role - the backend is the source of truth for which
// pages a role may hold, so this is only here to avoid refetching on every
// role change in the user form.
const allowedPagesCache: Record<string, RoleAllowedPages> = {}

export type TableConfig = {
  pricing?: string[]
  inventory?: string[]
  master?: string[]
}

// To Register a user in DB
const createUser = (data: any) => {
  return Api.post({ url: USER, data: data })
}

// To get all users from DB
const getAllUsers = ({
  limit,
  page,
  search,
}: {
  limit: number
  page: number
  search?: string
}) => {
  const options: any = {}

  if (search) {
    options.search = search
  }

  const params = new URLSearchParams({
    limit,
    page,
    ...options,
  })

  return Api.get({
    url: `${USER}?${params}`,
  })
}

// To get user from DB using ID
const getUser = (id: string) => {
  return Api.get({
    url: `${USER}/${id}`,
  })
}

// To update user in DB using ID
const updateUser = ({ id, data }: { id: string; data: any }) => {
  return Api.patch({
    url: `${USER}/${id}`,
    data,
  })
}

// To update the user specific columns
const updateTableCols = (data: TableConfig) => {
  return Api.patch({
    url: UPDATE_COLS,
    data,
  })
}

// To get the pages a given role is allowed to be granted. Replaces the
// picker-only lookup now that PICKER, FRESH_STOCKER and FRESH_REPORT are all
// restricted - the backend decides, the form just renders what it is told.
const getAllowedPagesByRole = async (
  role: string,
): Promise<RoleAllowedPages> => {
  if (allowedPagesCache[role]) {
    return allowedPagesCache[role]
  }

  const params = new URLSearchParams({ role })
  const { data } = await Api.get({ url: `${ALLOWED_PAGES}?${params}` })

  const result: RoleAllowedPages = {
    role,
    isRestricted: Boolean(data?.isRestricted),
    pages: data?.pages ?? [],
  }

  allowedPagesCache[role] = result

  return result
}

export type AssignableFreshCategory = { _id: string; name: string }

// Active Fresh categories a user can be assigned, for the user form's
// checklist. Not cached: a category created in another tab should show up on
// the next visit to this form.
const getAssignableFreshCategories = async (): Promise<
  AssignableFreshCategory[]
> => {
  const { data } = await Api.get({ url: ASSIGNABLE_FRESH_CATEGORIES })

  return data?.categories ?? []
}

export {
  createUser,
  getAllUsers,
  getUser,
  updateUser,
  updateTableCols,
  getAllowedPagesByRole,
  getAssignableFreshCategories,
}
